import React from 'react';

const RELOAD_FLAG_PREFIX = 'mc_chunk_reload:';

/**
 * React.lazy with recovery for stale-deploy chunk 404s.
 *
 * After a new Vercel deploy, hashed chunk filenames from the previous build
 * 404 for users mid-session; a bare React.lazy then throws into Suspense and
 * bricks the funnel. This wrapper retries the import once (covers transient
 * network blips), then falls back to a single full reload so the browser picks
 * up the new asset manifest. The reload is flagged per chunk in sessionStorage
 * to prevent reload loops when the chunk is genuinely gone.
 */
export function lazyWithRetry<T extends React.ComponentType<any>>(
    importFn: () => Promise<{ default: T }>,
    chunkName: string
): React.LazyExoticComponent<T> {
    return React.lazy(async () => {
        try {
            const mod = await importFn();
            try {
                sessionStorage.removeItem(RELOAD_FLAG_PREFIX + chunkName);
            } catch { /* storage disabled */ }
            return mod;
        } catch (firstErr) {
            await new Promise((resolve) => setTimeout(resolve, 1200));
            try {
                return await importFn();
            } catch (retryErr) {
                let alreadyReloaded = false;
                try {
                    alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG_PREFIX + chunkName) === '1';
                    if (!alreadyReloaded) {
                        sessionStorage.setItem(RELOAD_FLAG_PREFIX + chunkName, '1');
                    }
                } catch { /* storage disabled — reload once anyway is worse than erroring twice */ }
                if (!alreadyReloaded) {
                    window.location.reload();
                    // Keep the lazy promise pending while the page reloads.
                    return new Promise<never>(() => {});
                }
                throw retryErr;
            }
        }
    });
}
